import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGroup } from '../../contexts/GroupContext';
import { useToast } from '../../components/common';
import api from '../../services/api';
import { schemaLabel } from '../../utils/schemaUtils';

// STALE-WHILE-REVALIDATE (the missions pattern, owner call 2026-08-21): the
// round trip runs long enough that every visit opened on a spinner. The LAST
// viewed page is kept in localStorage — one entry, overwritten per fetch — so
// a revisit or a refresh paints the last known wall immediately and the fresh
// fetch re-renders underneath. The spinner only ever shows on a first visit,
// or on a query (page / filter / scope) the cache hasn't seen.
const GRAPH_CACHE_KEY = 'nucleus:graph-cache-v1';

function readGraphCache(queryKey) {
    try {
        const c = JSON.parse(localStorage.getItem(GRAPH_CACHE_KEY));
        return c && c.key === queryKey ? c : null;
    } catch { return null; }
}

function writeGraphCache(queryKey, nodes, total) {
    // Best-effort: an oversized page (quota) just doesn't cache.
    try { localStorage.setItem(GRAPH_CACHE_KEY, JSON.stringify({ key: queryKey, nodes, total, savedAt: Date.now() })); } catch { /* quota / private mode */ }
}

/**
 * Custom hook: manages data fetching for the graph page.
 * Loads nodes one page at a time from the server (server-side pagination + filtering).
 * Schemas and relationships are loaded separately as sidebar data.
 */
export default function useGraphData({ graphTab, access = 'owned', page = 0, pageSize = 25, filters = {}, catalogAppId = null }) {
    const { activeGroupId, isGroupContext } = useGroup();
    const [searchParams] = useSearchParams();
    const toast = useToast();
    const timestamp = searchParams.get('timestamp');
    const isTimeTravelMode = !!timestamp;

    // The cache key IS the query: scope + access + page + filters. Computed
    // before the state initializers so the very first paint can come warm.
    const filtersKey = JSON.stringify(filters);
    const queryKey = JSON.stringify([
        catalogAppId ? `catalog:${catalogAppId}` : (isGroupContext && activeGroupId ? activeGroupId : 'personal'),
        access, page, pageSize, filtersKey,
    ]);

    const [nodes, setNodes] = useState(() => readGraphCache(queryKey)?.nodes || []);
    const [total, setTotal] = useState(() => readGraphCache(queryKey)?.total || 0);
    const [relationships, setRelationships] = useState([]);
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(() => !readGraphCache(queryKey));
    const [schemaFilters, setSchemaFilters] = useState([]);
    const [relationshipTypes, setRelationshipTypes] = useState([]);
    // Incrementing this triggers a node re-fetch (e.g. after CRUD operations)
    const [reloadKey, setReloadKey] = useState(0);
    const [facets, setFacets] = useState(null);
    const [facetsLoading, setFacetsLoading] = useState(false);
    const [catalogAppName, setCatalogAppName] = useState(null);

    const schemasMap = useMemo(() => new Map(schemas.map(s => [s.id, s])), [schemas]);

    // Load sidebar data: schemas, relationships.
    // Re-runs when access changes but NOT on every page/filter change.
    useEffect(() => {
        if (graphTab !== 'database' || isTimeTravelMode) return;
        const ignore = { current: false };

        let dataPromise;
        if (catalogAppId) {
            // Catalog nodes are flat (phase 1, no relationships) — schemas stay
            // global (they aren't app-scoped), and the inventory is just the
            // whole catalog itself, capped at list_app_nodes's own 500 limit.
            dataPromise = Promise.all([
                Promise.resolve([]),
                api.getSchemas({ include_json_schema: 'false', limit: 1000 }).then((schemas) => ({ schemas })).catch((e) => { console.error('[nucleus] /schemas fetch failed:', e); return { schemas: [] }; }),
                api.getAppCatalogNodes(catalogAppId, { limit: 500 }).catch(() => ({ nodes: [] })),
            ]);
        } else if (isGroupContext && activeGroupId) {
            // Group context: load group-specific relationships and nodes for schema inventory
            dataPromise = Promise.all([
                api.getGroupRelationships(activeGroupId).catch(() => []),
                api.getSchemas({ include_json_schema: 'false', limit: 1000 }).then((schemas) => ({ schemas })).catch((e) => { console.error('[nucleus] /schemas fetch failed:', e); return { schemas: [] }; }),
                api.getGroupNodes(activeGroupId, { per_page: 500 }).catch(() => ({ nodes: [] })),
            ]);
        } else {
            const schemaInventoryParams = new URLSearchParams({
                access,
                fields: 'id,schema_id', limit: '5000', skip_total: 'true',
            });
            dataPromise = Promise.all([
                api.getRelationships({ access }).catch(() => []),
                api.getSchemas({ include_json_schema: 'false', limit: 1000 }).then((schemas) => ({ schemas })).catch((e) => { console.error('[nucleus] /schemas fetch failed:', e); return { schemas: [] }; }),
                api.request(`/nodes?${schemaInventoryParams}`).catch(() => ({ nodes: [] })),
            ]);
        }

        dataPromise.then(([relsData, schemasData, inventoryData]) => {
            if (ignore.current) return;
            const rels = relsData || [];
            setRelationships(rels);
            setRelationshipTypes([...new Set(rels.map(r => r.relationship_type))]);
            const allSchemas = schemasData?.schemas || [];
            if (!allSchemas.length) console.warn('[nucleus] schemas list empty — raw response:', schemasData);
            setSchemas(allSchemas);

            // Build schemaFilters only from schemas that have at least one node
            const schemaNameMap = new Map(allSchemas.map(s => [s.id, schemaLabel(s.schema_name)]));
            const usedSchemaIds = new Set();
            let hasNoSchema = false;
            const inventoryNodes = inventoryData?.nodes || [];
            inventoryNodes.forEach(n => {
                if (n.schema_id) usedSchemaIds.add(n.schema_id);
                else hasNoSchema = true;
            });
            const schemaList = [];
            if (hasNoSchema) schemaList.push({ id: '__none__', name: 'No Schema' });
            usedSchemaIds.forEach(id => schemaList.push({ id, name: schemaNameMap.get(id) || id.slice(0, 8) }));
            setSchemaFilters(schemaList);

            // For group context AND catalog mode, compute facets from the
            // inventory nodes client-side — neither has a server facets endpoint
            // (catalogs are small; the 500-node cap is the whole thing in practice).
            if (isGroupContext || catalogAppId) {
                const schemasCounts = {};
                const appsCounts = {};
                const relsCounts = {};
                inventoryNodes.forEach(n => {
                    const sk = n.schema_id || '__none__';
                    schemasCounts[sk] = (schemasCounts[sk] || 0) + 1;
                    if (n.created_by_app_name) appsCounts[n.created_by_app_name] = (appsCounts[n.created_by_app_name] || 0) + 1;
                });
                rels.forEach(r => { relsCounts[r.relationship_type] = (relsCounts[r.relationship_type] || 0) + 1; });
                setFacets({ schemas: schemasCounts, apps: appsCounts, relationships: relsCounts });
            }
        }).catch(() => {});
        return () => { ignore.current = true; };
    }, [graphTab, access, isTimeTravelMode, isGroupContext, activeGroupId, catalogAppId, reloadKey]);

    // Load nodes: re-runs on page, filter, access, or reload changes.
    useEffect(() => {
        if (graphTab !== 'database' || isTimeTravelMode) return;
        const ignore = { current: false };
        // Paint the last known page for THIS query while the fetch runs;
        // spinner only when the cache has nothing to show.
        const cached = readGraphCache(queryKey);
        if (cached) {
            setNodes(cached.nodes || []);
            setTotal(cached.total || 0);
            setLoading(false);
        } else {
            setLoading(true);
        }

        if (catalogAppId) {
            // list_app_nodes only supports schema_name server-side today — no
            // search/date/value_json filters to bind the rest of `filters` to.
            const catalogParams = { limit: pageSize, offset: page * pageSize };
            if (filters.schema_name) catalogParams.schema_name = filters.schema_name;

            api.getAppCatalogNodes(catalogAppId, catalogParams)
                .then(resp => {
                    if (ignore.current) return;
                    setNodes(resp.nodes || []);
                    setTotal(resp.total || 0);
                    setLoading(false);
                    if (resp.app?.name) setCatalogAppName(resp.app.name);
                    writeGraphCache(queryKey, resp.nodes || [], resp.total || 0);
                })
                .catch(err => {
                    if (!ignore.current) {
                        console.error('[useGraphData] catalog fetch failed:', err);
                        toast.error(err?.message || 'Failed to load the catalog.');
                        setLoading(false);
                    }
                });
        } else if (isGroupContext && activeGroupId) {
            const groupParams = {
                page: String(page + 1),
                per_page: String(pageSize),
            };
            if (filters.search) groupParams.search = filters.search;
            if (filters.channel_id) groupParams.channel_id = filters.channel_id;
            if (filters.channel_name) groupParams.channel_name = filters.channel_name;
            if (filters.subgroup_name) groupParams.subgroup_name = filters.subgroup_name;
            if (filters.schema_ids) groupParams.schema_ids = filters.schema_ids;
            if (filters.app_names) groupParams.app_names = filters.app_names;
            if (filters.relationship_types) groupParams.relationship_types = filters.relationship_types;
            if (filters.date_field) groupParams.date_field = filters.date_field;
            if (filters.date_after) groupParams.date_after = filters.date_after;
            if (filters.date_before) groupParams.date_before = filters.date_before;
            if (filters.sort) groupParams.sort = filters.sort;
            if (filters.order) groupParams.order = filters.order;

            api.getGroupNodes(activeGroupId, groupParams)
                .then(resp => {
                    if (ignore.current) return;
                    setNodes(resp.nodes || []);
                    setTotal(resp.total || 0);
                    setLoading(false);
                    writeGraphCache(queryKey, resp.nodes || [], resp.total || 0);
                })
                .catch(() => { if (!ignore.current) setLoading(false); });
        } else {
            const params = new URLSearchParams({
                access,
                limit: String(pageSize),
                offset: String(page * pageSize),
                skip_total: 'false',
                ...(access !== 'owned' ? { include_owner: 'true' } : {}),
            });
            if (filters.search) params.set('search', filters.search);
            if (filters.schema_ids) params.set('schema_ids', filters.schema_ids);
            if (filters.schema_id) params.set('schema_id', filters.schema_id);
            if (filters.value_json_field) params.set('value_json_field', filters.value_json_field);
            if (filters.value_json_value) params.set('value_json_value', filters.value_json_value);
            if (filters.value_json_match) params.set('value_json_match', filters.value_json_match);
            if (filters.relationship_types) params.set('relationship_types', filters.relationship_types);
            if (filters.app_names) params.set('app_names', filters.app_names);
            if (filters.sort) params.set('sort', filters.sort);
            if (filters.order) params.set('order', filters.order);
            if (filters.date_field) params.set('date_field', filters.date_field);
            if (filters.date_after) params.set('date_after', filters.date_after);
            if (filters.date_before) params.set('date_before', filters.date_before);

            api.request(`/nodes?${params.toString()}`)
                .then(resp => {
                    if (ignore.current) return;
                    setNodes(resp.nodes || []);
                    setTotal(resp.total || 0);
                    setLoading(false);
                    writeGraphCache(queryKey, resp.nodes || [], resp.total || 0);
                })
                .catch(err => {
                    if (!ignore.current) {
                        if (!err?.isAuthError) {
                            // eslint-disable-next-line no-console
                            console.error('[useGraphData] nodes fetch failed:', err);
                            toast.error(err?.message || 'Failed to load data. Please try again.');
                        }
                        setLoading(false);
                    }
                });
        }
        return () => { ignore.current = true; };
    }, [graphTab, access, page, pageSize, filtersKey, isGroupContext, activeGroupId, catalogAppId, isTimeTravelMode, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Facets fetch: server-side GROUP BY aggregations for accurate filter histograms.
    // Runs when filters or access change (not on page change). No row limit.
    useEffect(() => {
        if (graphTab !== 'database' || isTimeTravelMode || isGroupContext || catalogAppId) {
            setFacetsLoading(false);
            return;
        }
        const ignore = { current: false };
        const params = new URLSearchParams({ access });
        if (filters.search) params.set('search', filters.search);
        if (filters.schema_ids) params.set('schema_ids', filters.schema_ids);
        if (filters.schema_id) params.set('schema_id', filters.schema_id);
        if (filters.value_json_field) params.set('value_json_field', filters.value_json_field);
        if (filters.value_json_value) params.set('value_json_value', filters.value_json_value);
        if (filters.value_json_match) params.set('value_json_match', filters.value_json_match);
        if (filters.relationship_types) params.set('relationship_types', filters.relationship_types);
        if (filters.date_field) params.set('date_field', filters.date_field);
        if (filters.date_after) params.set('date_after', filters.date_after);
        if (filters.date_before) params.set('date_before', filters.date_before);
        setFacetsLoading(true);
        api.request(`/nodes/facets?${params.toString()}`)
            .then(resp => { if (!ignore.current) { setFacets(resp); setFacetsLoading(false); } })
            .catch(() => { if (!ignore.current) setFacetsLoading(false); });
        return () => { ignore.current = true; };
    }, [graphTab, access, filtersKey, isTimeTravelMode, isGroupContext, catalogAppId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Time travel mode
    useEffect(() => {
        if (graphTab !== 'database' || !isTimeTravelMode) return;
        const ignore = { current: false };
        setLoading(true);
        api.getGraphSnapshot(timestamp).then(snapshot => {
            if (ignore.current) return;
            const allNodes = snapshot.nodes || [];
            setNodes(allNodes.slice(page * pageSize, (page + 1) * pageSize));
            setTotal(allNodes.length);
            setRelationships(snapshot.relationships || []);
            setRelationshipTypes([...new Set((snapshot.relationships || []).map(r => r.relationship_type))]);
            const ids = new Set();
            allNodes.forEach(n => { if (n.schema_id) ids.add(n.schema_id); });
            setSchemaFilters([...[...ids].map(id => ({ id, name: id.slice(0, 8) }))]);
        }).catch(() => toast.error('Failed to load snapshot.')).finally(() => { if (!ignore.current) setLoading(false); });
        return () => { ignore.current = true; };
    }, [graphTab, isTimeTravelMode, timestamp, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const reload = useCallback(() => setReloadKey(k => k + 1), []);
    // Alias for useNodeCRUD compatibility (ignores the {ignore} cancellation arg)
    const loadIdentityGraph = useCallback(() => setReloadKey(k => k + 1), []);

    return {
        nodes, total, relationships, schemas, schemasMap, loading,
        isTimeTravelMode, timestamp,
        schemaFilters, relationshipTypes,
        facets, facetsLoading,
        catalogAppName,
        reload, loadIdentityGraph, setNodes,
    };
}
